import React from "react";
import classnames from "classnames";

import "./index.scss";

export interface FilterChipItem<TValue extends string | number> {
  value: TValue;
  label: string;
  // all-recognitions-screen-plan.md "The chips ship without counts, deliberately": the per-value
  // `GROUP BY core_value_id` endpoint the All Recognitions screen would read from doesn't exist yet (a
  // follow-up api PR) -- optional, not defaulted, so an absent count renders no number at all,
  // distinguishable from a real, fetched zero. No chip may render a fabricated number. The All Badges
  // screen's own categories always have a real count (read off the catalog already on the page via
  // `buildCategorySummaries`), so that caller simply always supplies one -- this field stays optional
  // so both callers can share one component rather than forking it over that difference.
  count?: number;
}

interface FilterChipsProps<TValue extends string | number> {
  items: FilterChipItem<TValue>[];
  selected?: TValue;
  onSelect: (value?: TValue) => void;
  // Mirrors each item's own optional count, for the identical reason given on `FilterChipItem.count`.
  allCount?: number;
  // 923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md's own L5: the Leaderboard tab's frame labels this chip "All
  // values", not the generic "All" the All Recognitions/All Badges screens already ship. Optional,
  // defaulting to "All" -- so BOTH existing callers keep their exact current copy without passing
  // anything, and only the new caller opts into different wording. Never change the default itself to
  // "fix" this for one caller; that silently restyles the other two.
  allLabel?: string;
}

const chipLabel = (label: string, count: number | undefined): string =>
  typeof count === "number" ? `${label} · ${count}` : label;

/**
 * A single-select row of filter chips -- "All" plus one chip per item -- shared by the All Recognitions
 * screen (filtering by core value) and the All Badges screen (filtering by badge category)
 * (all-recognitions-screen-plan.md / all-badges-screen-plan.md). Generic over `TValue` rather than two
 * near-identical components: a core value's id is a `number`, a badge category is a string enum, and
 * every other detail -- the empty guard, the "All" chip, `classnames({ selected })`, the single-select
 * click semantics, the `label · count` format -- was, and remains, byte-identical between the two.
 *
 * Not a `React.FC` -- that type cannot express a type parameter, so this is a plain generic function
 * component instead, mirroring how this file's own JSDoc frames the type: one shared implementation,
 * inferred per call site from `items`/`selected`/`onSelect` together.
 *
 * Renders as a single-select group: a value chip always SETS its value on click, "All" is the dedicated
 * way to clear -- never a toggle-per-chip (this differs deliberately from `ValueLadderCards`'
 * click-to-filter cards, which have no "All" option and so must toggle). Presentational only -- no
 * dispatch/select of its own, the caller owns all dispatching.
 */
const FilterChips = <TValue extends string | number>(props: FilterChipsProps<TValue>): JSX.Element | null => {
  const { items, selected, onSelect, allCount, allLabel = "All" } = props;

  // No catalog, no row. A lone "All" chip would claim a filter set of exactly one option and do
  // nothing when clicked -- rendering nothing is honest about having no filters to offer.
  //
  // This is also what renders while a catalog fetch is in flight, and what it would render FOREVER if
  // that fetch fails: the All Recognitions caller's core-values fetch has no `getCoreValues.failure`
  // handler at all (follow-up 111), so a failed catalog is indistinguishable here from an empty one.
  // Rendering nothing is honest about having no filters to offer; it is still silent about the
  // failure, which is what that follow-up is for. (The All Badges caller derives its items from the
  // badge catalog already on the page, which has its own failure note, so it is not affected.)
  if (!items.length) {
    return null;
  }

  return (
    <div className="filter-chips">
      <button
        type="button"
        className={classnames("filter-chip", { selected: selected === undefined })}
        onClick={() => onSelect(undefined)}
      >
        {chipLabel(allLabel, allCount)}
      </button>
      {items.map((item) => (
        <button
          key={item.value}
          type="button"
          className={classnames("filter-chip", { selected: selected === item.value })}
          onClick={() => onSelect(item.value)}
        >
          {chipLabel(item.label, item.count)}
        </button>
      ))}
    </div>
  );
};

export default FilterChips;
